/* eslint-disable react/forbid-prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { MdPersonAdd } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import PropTypes from 'prop-types';

import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import CardSideBordered from '~/components/CardSideBordered';
import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';
import UserForm from '~/components/UserForm';

import history from '~/services/history';
import api from '~/services/pluginbot-api';
import { expireSession } from '~/store/modules/auth/actions';

export default function GroupUserForm(props) {
    const dispatch = useDispatch();
    const { match } = props;
    const { id, user_id } = match.params;
    const [operation, setOperation] = useState('create');

    const [body, setBody] = useState({
        group_id: id,
        user_id: null,
        permission: 'viewer',
    });

    const [newUser, setNewUser] = useState({
        permission: 'editor',
    });

    const [isLoading, setIsLoading] = useState(true);
    const [forbidden, setForbidden] = useState(false);
    const [users, setUsers] = useState([]);

    const permissions = [
        {
            label: <LocaleMessage msg="user.permission.viewer" />,
            value: 'viewer',
        },
        {
            label: <LocaleMessage msg="user.permission.editor" />,
            value: 'editor',
        },
        {
            label: <LocaleMessage msg="user.permission.admin" />,
            value: 'admin',
        },
    ];

    function requestError(error) {
        if (error.response) {
            const message = (
                <LocaleMessage msg={`errors.${error.response.data.code}`} />
            );
            const { status } = error.response;
            if (status === 401) {
                dispatch(expireSession());
            } else if (status === 403) {
                setForbidden(true);
            }
            toast.error(message);
        } else if (error.request) {
            toast.error(<LocaleMessage msg="errors.request" />);
        } else {
            toast.error(<LocaleMessage msg="errors.unknown" />);
        }
        setIsLoading(false);
    }

    async function loadUsers() {
        await api
            .get(`users`)
            .then(response => {
                const userList = response.data;
                setUsers(userList);
            })
            .catch(error => requestError(error));
    }

    async function loadData() {
        if (user_id === 'new') {
            setOperation('create');
            await loadUsers();
        } else {
            setOperation('update');
            await api
                .get(`groups/${id}/users/${user_id}`)
                .then(resp => {
                    const u = resp.data.user;
                    setBody({
                        group_id: id,
                        email: u.email,
                        permission: u.permission,
                    });
                })
                .catch(error => requestError(error));
        }

        setTimeout(() => {
            setIsLoading(false);
        }, 100);
    }

    useEffect(() => {
        loadData();
    }, []);

    function onUserOpChange(event) {
        const new_user = event.target.value;
        return setBody({ ...body, new_user });
    }

    function onPermissionChange(event) {
        setBody({ ...body, permission: event.target.value });
    }

    async function handleSubmit(event) {
        event.preventDefault();

        setIsLoading(true);

        const data = {
            ...body,
            ...(body.new_user && { user_id: null, user: newUser }),
        };

        if (operation === 'create') {
            await api
                .post(`groups/users`, data)
                .then(() => {
                    setIsLoading(false);

                    toast.success(
                        <LocaleMessage msg="page.groupuser.form.create_success" />
                    );
                    history.push(`/pluginspace/groups/${id}`);
                })
                .catch(error => requestError(error));
        } else {
            await api
                .put(`groups/${id}/users/${user_id}`, data)
                .then(() => {
                    setIsLoading(false);
                    toast.success(
                        <LocaleMessage msg="page.groupuser.form.update_success" />
                    );
                })
                .catch(error => requestError(error));
        }
    }

    function renderExistingUser() {
        if (operation === 'create') {
            return (
                <div className="col-md-6 col-12 mb-5">
                    <Autocomplete
                        options={users}
                        getOptionLabel={option =>
                            option.email
                                ? `${option.name} (${option.email})`
                                : ''
                        }
                        filterOptions={(options, { inputValue }) => {
                            const filterValue = inputValue.toLowerCase();
                            return options.filter(
                                option =>
                                    option.email
                                        ?.toLowerCase()
                                        .includes(filterValue) ||
                                    option.name
                                        ?.toLowerCase()
                                        .includes(filterValue)
                            );
                        }}
                        value={users.find(u => u.email === body.email) || null}
                        onChange={(event, newValue) => {
                            setBody({
                                ...body,
                                email: newValue ? newValue.email : '',
                                user_id: newValue ? newValue.id : null,
                            });
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label={<LocaleMessage msg="user.email" />}
                                fullWidth
                            />
                        )}
                    />
                </div>
            );
        }

        return (
            <div className="col-md-6 col-12 mb-5">
                <TextField
                    label={<LocaleMessage msg="user.email" />}
                    value={body.email || ''}
                    fullWidth
                    disabled
                />
            </div>
        );
    }

    function renderNewUser() {
        return <UserForm user={newUser} setUser={u => setNewUser(u)} />;
    }

    return (
        <PageContent
            title={
                operation === 'create' ? (
                    <LocaleMessage msg="page.groupuser.form.create.title" />
                ) : (
                    <LocaleMessage msg="page.groupuser.form.edit.title" />
                )
            }
            breadcrumbs={[
                {
                    url: '/',
                    title: <LocaleMessage msg="breadcrumbs.home" />,
                },
                {
                    url: '/pluginspace/groups',
                    title: <LocaleMessage msg="breadcrumbs.groups" />,
                },
                {
                    url: `/pluginspace/groups/${id}`,
                    title: <LocaleMessage msg="breadcrumbs.usergroup" />,
                },
            ]}
            loading={isLoading}
            forbidden={forbidden}
        >
            <>
                <form className="row full-body" noValidate autoComplete="off">
                    <div className="col-md-8 col-12 mb-5">
                        <CardSideBordered
                            title={
                                <LocaleMessage msg="page.groupuser.form.label.title" />
                            }
                            Icon={MdPersonAdd}
                        >
                            <>
                                {operation === 'create' ? (
                                    <div className="row">
                                        <div className="col-md-6 col-12 mb-5">
                                            <FormControl fullWidth>
                                                <InputLabel>
                                                    <LocaleMessage msg="user.source" />
                                                </InputLabel>
                                                <Select
                                                    value={
                                                        body.new_user
                                                            ? body.new_user
                                                            : false
                                                    }
                                                    onChange={event =>
                                                        onUserOpChange(event)
                                                    }
                                                >
                                                    <MenuItem value={false}>
                                                        <LocaleMessage msg="user.source.existing" />
                                                    </MenuItem>
                                                    <MenuItem value>
                                                        <LocaleMessage msg="user.source.new" />
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                ) : null}
                                <div className="row">
                                    {body.new_user
                                        ? renderNewUser()
                                        : renderExistingUser()}

                                    <div className="col-md-6 col-12 mb-5">
                                        <FormControl
                                            error={body.permission === null}
                                            fullWidth
                                        >
                                            <InputLabel>
                                                <LocaleMessage msg="page.groupuser.form.label.privileges" />
                                            </InputLabel>
                                            <Select
                                                id="usergroup-permission"
                                                value={
                                                    body.permission
                                                        ? body.permission
                                                        : 'viewer'
                                                }
                                                onChange={event =>
                                                    onPermissionChange(event)
                                                }
                                            >
                                                {permissions.map(p => {
                                                    return (
                                                        <MenuItem
                                                            value={p.value}
                                                            key={p.value}
                                                        >
                                                            {p.label}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </FormControl>
                                    </div>
                                </div>
                            </>
                        </CardSideBordered>
                    </div>

                    <div className="col-md-8 col-12 mb-5">
                        <Button
                            className="p-3"
                            variant="contained"
                            color="primary"
                            onClick={event => handleSubmit(event)}
                            fullWidth
                            size="large"
                        >
                            <LocaleMessage msg="button.save" />
                        </Button>
                    </div>
                </form>
            </>
        </PageContent>
    );
}

GroupUserForm.propTypes = {
    match: PropTypes.object.isRequired,
};
